import * as React from 'react';
import ctrlImage from '../Assets/ctrl.png';
import boldvuIcon from '../Assets/boldvu.png';
import { bind } from '../Utils/Bind';
import { IError } from '../Utils/ErrorHandle';
import { ErrorStore } from '../Utils/ErrorStore';
import { BsArrowClockwise } from '@react-icons/all-files/bs/BsArrowClockwise';
import { BsCaretDownFill } from '@react-icons/all-files/bs/BsCaretDownFill';
import { BsFillEnvelopeFill } from '@react-icons/all-files/bs/BsFillEnvelopeFill';
import { BsHouseDoorFill } from '@react-icons/all-files/bs/BsHouseDoorFill';
import { BsBriefcase } from '@react-icons/all-files/bs/BsBriefcase';

export interface IErrorViewProps {
    error?: IError;
    errorStore?: ErrorStore;
}

interface IErrorViewState {
    error?: IError;
    errorInfo?: React.ErrorInfo;
    collapsed: boolean;
}

export class ErrorView extends React.Component<IErrorViewProps, IErrorViewState> {
    public constructor(props: IErrorViewProps) {
        super(props);

        this.state = {
            collapsed: true,
        };
    }

    public componentDidMount() {
        window.onerror = this.onError;
        window.addEventListener('unhandledrejection', this.onUnhandledRejection);
    }

    public async componentDidUpdate() {
        const error = this.props.error ?? this.state.error;

        if (error && this.props.errorStore) {
            try {
                await this.props.errorStore.logError(error);
            } catch (e: any) {
                console.error('Failed to log error');
            }
        }
    }

    public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        const errorObject: IError = {
            message: error.message,
            file: error.name,
            stack: error.stack,
        };

        this.setState({
            error: errorObject,
            errorInfo,
        });

        console.error(error);
    }

    public render() {
        if (this.state.error ?? this.props.error) return this.renderErrorPage();
        else return this.props.children;
    }

    private renderErrorPage() {
        this.removeTheme();
        return (
            <div className="container-fluid h-100">
                <div className="row justify-content-center bg-white box-shadow-bottom">
                    <div className="col-6 col-md-3 col-lg-2 text-center py-2">
                        <img src={boldvuIcon} className="img-fluid mw-150" />
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-6 order-2 order-md-1">
                        <div className="flex-items-center h-100 w-100">
                            <div className="text-start ps-md-5">
                                <h1 className="error-header-text">Uh oh! Looks like we lost CTRL!</h1>

                                <h5 className="d-inline-block">
                                    Sorry for the mishap. The error has been logged and is being investigated. Here are
                                    some options to get you going in the meantime:
                                </h5>
                                <div>
                                    <BsHouseDoorFill />
                                    <strong> Go back </strong>
                                    <a href="/">Home</a>
                                </div>

                                <div>
                                    <BsArrowClockwise />
                                    <strong />
                                    <a href="#" onClick={this.refreshPage}>
                                        Refresh
                                    </a>
                                    <strong> the page</strong>
                                </div>

                                <div>
                                    <BsBriefcase />
                                    <strong />
                                    <a href="https://status.mri-inc.net">Check</a>
                                    <strong> SmartVu® Status </strong>
                                </div>

                                <div>
                                    <BsFillEnvelopeFill />
                                    <strong> Email us at </strong>{' '}
                                    <a type="email" href="customerservice@mri-inc.net">
                                        customerservice@mri-inc.net
                                    </a>
                                </div>

                                <br />

                                {this.renderError()}
                            </div>
                        </div>
                    </div>

                    <div className="col-md-6 order-1 order-md-2">
                        <div className="flex-items-center h-100 text-center">
                            <img src={ctrlImage} className="img-fluid" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderError() {
        const { error, errorInfo, collapsed } = this.state;
        const lines = error?.message.split('\n');
        const firstLine = lines ? lines[0] : undefined;
        const otherLines = lines?.slice(1);
        const expandable = !!otherLines?.length ?? !!error?.stack ?? !!errorInfo;

        return (
            <div>
                {firstLine && (
                    <div className="text-danger" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                        {firstLine}

                        {expandable && (
                            <a onClick={this.toggleError} className="btn btn-link">
                                <BsCaretDownFill />
                            </a>
                        )}
                    </div>
                )}

                {!collapsed && !!otherLines?.length && (
                    <div className="text-danger" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                        {otherLines.join('\n')}
                    </div>
                )}

                {!collapsed && error?.stack && (
                    <div className="text-danger" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                        {error.stack}
                    </div>
                )}

                {!collapsed && errorInfo && (
                    <div className="text-danger" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                        {errorInfo.componentStack}
                    </div>
                )}
            </div>
        );
    }

    @bind
    private onError(event: Event | string, source?: string, fileno?: number, columnNumber?: number, error?: Error) {
        if (event instanceof CustomEvent && event.defaultPrevented) return;
        if (event == 'ResizeObserver loop limit exceeded') return;

        console.error(error);
        columnNumber = columnNumber ?? (window.event as any)?.errorCharacter;
        let stack = error?.stack;

        //trying to get stack from IE
        try {
            if (!stack) {
                const s: string[] = [];
                // eslint-disable-next-line no-caller
                let f = arguments.callee.caller;
                while (f) {
                    s.push(f.name);
                    f = f.caller;
                }

                stack = s.join('\n');

                if (error) error.stack = stack;
            }
        } catch (e: any) {
            // Do nothing
        }

        const errorObject: IError = {
            message:
                event instanceof CustomEvent
                    ? event.detail.detailedMessage ?? event.detail.message ?? JSON.stringify(event.detail)
                    : event instanceof Event
                        ? event.toString()
                        : event,
            file: source,
            line: fileno,
            column: columnNumber,
            stack,
        };

        // TODO send error to C#

        // If no error passed in via props
        if (!this.props.error)
            this.setState({
                error: errorObject,
                errorInfo: undefined,
            });

        return true;
    }

    @bind
    private onUnhandledRejection(event: any) {
        const error: Error = event.reason || event.detail.reason;

        const errorObject: IError = {
            message: error.message,
            file: error.name,
            stack: error.stack,
        };

        // If no error passed in via props
        if (!this.props.error)
            this.setState({
                error: errorObject,
                errorInfo: undefined,
            });

        return true;
    }

    @bind
    private removeTheme() {
        try {
            const el = document.getElementById('MainBody');

            if (el) {
                el.classList.remove('theme-1');
                el.classList.remove('theme-2');
                el.classList.remove('dark-theme');
            }
        } catch (e: any) {
            console.error(e);
        }
    }

    @bind
    private toggleError() {
        this.setState(prevState => {
            return { collapsed: !prevState.collapsed };
        });
    }

    @bind
    private refreshPage(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();

        location.reload();
    }
}
